import { DynamicContainer, Form, FormSection } from '@hai/ui-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormSwitch } from 'src/js/component/base/form/form-switch';
import systemInfo from 'src/js/data/systemInfo';
import { Service } from 'src/js/model/service';
import { ServicesViewModel } from 'src/js/pages/settings/services/services-view-model';
import { generateDataAuto } from 'src/js/util/automation';
import { FormChangeEvent } from 'src/js/util/global-type';

import { NicOptions } from './services';
import { removeItemFromArray } from 'src/js/util/array-util';
import { ServiceToggle } from './service-toggle';

interface Props {
  services: ServicesViewModel;
  onServiceChange: (service: Service, value: boolean) => Promise<any> | void;
  onServiceNicChange: (event: FormChangeEvent) => void;
  nicOptions: NicOptions;
  loading: boolean;
}

export const GeneralServices = (props: Props) => {
  const { services, onServiceChange, onServiceNicChange, nicOptions, loading } = props;
  const { t } = useTranslation();

  const renderServiceLabel = (service: { key: string }) => t(`settings.services.section.general.${service.key}`);
  const servicesList = [
    { key: Service.EMS, disabled: false },
    { key: Service.HTTP },
    { key: Service.NMOS, condition: systemInfo.personality === '2110' },
    { key: Service.SNMP },
    { key: Service.SSH },
    { key: Service.TELNET },
  ];

  let servicesListCleaned = servicesList;
  //Clean non-applicable servces
  if (systemInfo.personality !== '2110') {
    servicesListCleaned = removeItemFromArray(servicesList, (service) => service.key === 'nmos');
  }

  return (
    <Form className="hai-mb-6">
      <FormSection
        title={t('settings.services.section.general.title')}
        data-auto={generateDataAuto('form', 'section general')}
      >
        {systemInfo.hasEth1 ? (
          <>
            {servicesListCleaned.map((service) => {
              const serviceKey = service.key as keyof ServicesViewModel;
              const nicKey = service.key as keyof typeof services.nic;
              return (
                <ServiceToggle
                  key={service.key}
                  serviceKey={service.key}
                  serviceLabel={renderServiceLabel(service)}
                  dropdownLabel={t('settings.services.section.general.interface')}
                  checked={!!services[serviceKey]}
                  loading={loading}
                  nicValue={t(`settings.network.options.${services.nic[nicKey] || 'all'}`)}
                  nicOptions={nicOptions}
                  onServiceChange={(checked: boolean) => onServiceChange(service.key, checked)}
                  onServiceNicChange={onServiceNicChange}
                  disabled={service.disabled}
                />
              );
            })}
          </>
        ) : (
          <>
            <DynamicContainer minColumns={6} maxColumns={6}>
              {servicesListCleaned.map((service) => {
                return (
                  <FormSwitch
                    key={service.key}
                    checked={!!services[service.key as keyof ServicesViewModel]}
                    loading={loading}
                    switchLabel={renderServiceLabel(service)}
                    onChange={(checked: boolean) => onServiceChange(service.key, checked)}
                    disabled={service.disabled}
                  />
                );
              })}
            </DynamicContainer>
          </>
        )}
      </FormSection>
    </Form>
  );
};
